import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Store, createSelector } from '@ngrx/store';
import { SchedulerThemeId, SchedulerThemeService } from './scheduler-theme.service';
import { IAppState } from './store/store.dto';

/** The app-wide look. Classic is the Fury theme; Steel is the redesign being migrated page by page. */
export type AppThemeId = 'classic' | 'steel';

/** Steel's header ground. Steel is the design default; light is kept as an alternate. */
export type AppHeaderStyle = 'steel' | 'light';

/**
 * License claim that shows the theme pickers (the account menu's Steel entry and the scheduler cog
 * menus' Color Theme section, custom-theme editor included). Without it, saved choices are ignored
 * and everyone gets the server default. Mirrors `ModuleNames.Module.ThemePicker` on the server.
 */
export const THEME_PICKER_CLAIM = 'pro.theme-picker';

const THEME_KEY = 'appTheme';
const HEADER_KEY = 'appHeaderStyle';
const NAV_PINNED_KEY = 'appNavPinned';

/**
 * Classic, deliberately: Steel is opt-in from the account menu until enough pages have moved over
 * that a mix of the two no longer looks broken. A demo server can change this for users who
 * haven't picked a theme, via the `defaultAppTheme` system preference.
 */
const DEFAULT_THEME: AppThemeId = 'classic';

/** Null-safe: the preferences arrive with the server sync, after this service starts. */
const selectDefaultAppTheme = createSelector(
  (state: IAppState) => state.data?.collections?.SystemPreferences?.defaultAppTheme,
  (theme): AppThemeId => (theme === 'steel' ? 'steel' : DEFAULT_THEME)
);

/**
 * Whether the user may pick themes, or null until the session arrives. Kept apart from
 * `UserHasClaim`, which says false before then too, so a demo user's saved Steel doesn't flash
 * Classic on every load while the session is on its way.
 */
const selectCanPickTheme = createSelector(
  (state: IAppState) => state.data?.UserSession,
  (session): boolean | null =>
    session ? session.claims.some((c) => c.toLowerCase() === THEME_PICKER_CLAIM) : null
);

/** The scheduler direction that goes with each app theme. */
function schedulerThemeFor(theme: AppThemeId): SchedulerThemeId {
  return theme === 'steel' ? 'steel-filled' : 'classic';
}

/**
 * Owns which app theme is active, plus the Steel shell's own per-user choices (header ground and
 * whether the side nav is pinned open). All of it is remembered in localStorage.
 *
 * Like SchedulerThemeService, the theme is one attribute on `<html>`: every Steel rule in
 * `styles/steel` is scoped to `[data-app-theme='steel']`, so switching reflows the styles without a
 * re-render. The layout also reads `isSteel()` to choose between the Fury shell and the Steel one.
 */
@Injectable({ providedIn: 'root' })
export class AppThemeService {
  private readonly schedulerTheme = inject(SchedulerThemeService);
  private readonly store = inject(Store);

  /**
   * The theme the user picked, now or on an earlier visit. It wins over the server default, except
   * while their license lacks the theme-picker claim: the picker is hidden then, so a saved choice
   * would otherwise stick with no way to change it.
   */
  private chosen: AppThemeId | null = null;

  private readonly _theme = signal<AppThemeId>(DEFAULT_THEME);
  private readonly _headerStyle = signal<AppHeaderStyle>('steel');
  private readonly _navPinned = signal(true);

  readonly theme = this._theme.asReadonly();
  readonly isSteel = computed(() => this._theme() === 'steel');
  readonly headerStyle = this._headerStyle.asReadonly();
  readonly navPinned = this._navPinned.asReadonly();

  constructor() {
    const theme = this.read(THEME_KEY);
    const header = this.read(HEADER_KEY);
    this._headerStyle.set(header === 'light' ? 'light' : 'steel');
    this._navPinned.set(this.read(NAV_PINNED_KEY) !== 'false');
    this.chosen = theme === 'steel' || theme === 'classic' ? theme : null;
    this.apply(this.chosen ?? DEFAULT_THEME);

    const serverDefault = this.store.selectSignal(selectDefaultAppTheme);
    const canPick = this.store.selectSignal(selectCanPickTheme);
    effect(() => {
      const preferred = serverDefault();
      const locked = canPick() === false;
      this.apply(locked ? preferred : (this.chosen ?? preferred));
      this.schedulerTheme.useAsDefault(schedulerThemeFor(preferred));
      this.schedulerTheme.setLocked(locked);
    });
  }

  /**
   * Switching the app theme also switches the scheduler to its matching direction. Only here, on an
   * explicit switch, so a user who then picks another scheduler theme from its cog menu keeps it.
   */
  setTheme(theme: AppThemeId): void {
    this.chosen = theme;
    this.apply(theme);
    this.write(THEME_KEY, theme);
    this.schedulerTheme.set(schedulerThemeFor(theme));
  }

  toggleSteel(): void {
    this.setTheme(this.isSteel() ? 'classic' : 'steel');
  }

  setHeaderStyle(style: AppHeaderStyle): void {
    this._headerStyle.set(style);
    this.write(HEADER_KEY, style);
  }

  setNavPinned(pinned: boolean): void {
    this._navPinned.set(pinned);
    this.write(NAV_PINNED_KEY, String(pinned));
  }

  toggleNavPinned(): void {
    this.setNavPinned(!this._navPinned());
  }

  private apply(theme: AppThemeId): void {
    this._theme.set(theme);
    document.documentElement.setAttribute('data-app-theme', theme);
  }

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      // Private browsing: the choice still applies for this session.
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Private browsing or a full quota: the choice applies, it just is not remembered.
    }
  }
}
